import React, { useMemo, useState } from "react";
import axios from "axios";
import { getApiUrl } from "../../../../config/api";
import { useAuth } from "../../../../firebase/AuthContext";
import {
  AVATARS,
  getAvatar,
} from "../../../../embed/ChatWidget/components/avatar/avatars";

const BOT_API = `${getApiUrl()}/api/bot-dashboard`;
const authHeader = (token) => ({ Authorization: `Bearer ${token}` });

const buildForm = (bot) => ({
  bot_name: bot.bot_name,
  primary_color: bot.primary_color,
  // Bots saved against a retired placeholder id resolve to the default.
  avatar_id: getAvatar(bot.avatar_id).id,
  welcome_message: bot.welcome_message || "Hi! How can I help you today?",
  style_preset: bot.style_preset || "rounded",
  starter_prompts: (() => {
    const p = bot.starter_prompts || [];
    return [p[0] || "", p[1] || "", p[2] || ""];
  })(),
});

const SettingsTab = ({ bot, token, onSaved, onDeleted, onFormChange }) => {
  const { hasRole } = useAuth();
  const canEdit = hasRole("webchatbot.settings.edit");
  const canDelete = hasRole("webchatbot.delete");
  const [form, setForm] = useState(() => buildForm(bot));
  // What is actually stored on the server. Everything the widget shows is
  // edited live in the preview, so without this there is nothing to tell the
  // user their changes only exist in the browser until they press Save.
  const [savedForm, setSavedForm] = useState(() => buildForm(bot));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [message, setMessage] = useState("");

  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(savedForm),
    [form, savedForm],
  );

  const updateForm = (next) => {
    setForm(next);
    // Drop any previous result: leaving "Saved successfully" sitting above a
    // form that has since been edited says the opposite of what is true.
    setMessage("");
    onFormChange?.(next);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    try {
      const payload = {
        ...form,
        starter_prompts: form.starter_prompts.filter(Boolean),
      };
      await axios.put(`${BOT_API}/${bot.bot_id}`, payload, {
        headers: authHeader(token),
      });
      setMessage("Saved successfully.");
      // The form is now what the server holds, so the Save button settles back
      // to its "nothing to save" state.
      setSavedForm(form);
      onSaved({ ...bot, ...payload });
    } catch {
      setMessage("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${BOT_API}/${bot.bot_id}`, {
        headers: authHeader(token),
      });
      onDeleted(bot.bot_id);
    } catch {
      setMessage("Failed to delete. Please try again.");
      setDeleting(false);
    }
  };

  return (
    <div className="flex gap-8 items-start">
      {/* ── 左欄：基本設定 ─────────────────────────────────────── */}
      {/* Sticky inside the modal's scroll area: most editing happens in the
          right column, and Save is no use to anyone who has scrolled past it. */}
      <div className="space-y-5 w-72 shrink-0 sticky top-0 self-start">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
          Basic Settings
        </p>

        {/* Bot name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Bot Name
          </label>
          <input
            type="text"
            value={form.bot_name}
            onChange={(e) => updateForm({ ...form, bot_name: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E78A24] bg-white text-gray-900"
          />
        </div>

        {/* Primary colour */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Primary Colour
          </label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={form.primary_color}
              onChange={(e) =>
                updateForm({
                  ...form,
                  primary_color: e.target.value,
                })
              }
              className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
            />
            <span className="text-sm text-gray-500 font-mono">
              {form.primary_color}
            </span>
          </div>
        </div>

        {/* AI Agent (readonly placeholder) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            AI Agent
          </label>
          <input
            type="text"
            value="Glo-pal Default Agent"
            disabled
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-400 cursor-not-allowed"
          />
          <p className="text-xs text-gray-400 mt-1">
            自訂 Agent 為 Enterprise 功能，請聯絡我們升級。
          </p>
        </div>

        {message && (
          <p
            className={`text-sm ${message.includes("Failed") ? "text-red-500" : "text-green-600"}`}
          >
            {message}
          </p>
        )}

        {/* Unsaved-changes banner — the edits above and in the live preview
            are browser-only until Save is pressed, so say so plainly. */}
        {isDirty && !saving && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
            <span className="relative flex h-2 w-2 mt-1.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
            </span>
            <p className="text-xs text-amber-800 leading-snug">
              <span className="font-semibold">You have unsaved changes.</span>{" "}
              Click <span className="font-semibold">Save Changes</span> to apply
              them to your bot.
            </p>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          {canEdit && (
          <button
            onClick={handleSave}
            disabled={saving || !isDirty}
            title={isDirty ? "Save your changes" : "No changes to save"}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              isDirty && !saving
                ? "text-white ring-2 ring-offset-2 ring-[#E78A24] hover:brightness-110 hover:-translate-y-px"
                : "text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed"
            }`}
            style={
              isDirty && !saving
                ? {
                    backgroundColor: "#E78A24",
                    boxShadow: "0 6px 18px rgba(231,138,36,0.45)",
                  }
                : undefined
            }
          >
            {saving ? (
              <>
                <svg
                  className="w-4 h-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="3"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  />
                </svg>
                Saving…
              </>
            ) : isDirty ? (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
                Save Changes
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                All changes saved
              </>
            )}
          </button>
          )}

          {canDelete && (!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              className="px-5 py-2 rounded-lg text-red-600 border border-red-300 text-sm hover:bg-red-50 transition-colors"
            >
              Delete Bot
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm text-red-600">Are you sure?</span>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-1.5 bg-red-600 text-white text-sm rounded-lg disabled:opacity-50"
              >
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-4 py-1.5 text-sm text-gray-600 hover:underline"
              >
                Cancel
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ── 右欄：個性化 + Live Preview ────────────────────────── */}
      <div className="flex-1 space-y-5 min-w-0">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
          Personalization
        </p>

        {/* Avatar */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Avatar
          </label>
          <div className="flex gap-3 flex-wrap">
            {AVATARS.map((av) => {
              const selected = form.avatar_id === av.id;
              return (
                <button
                  key={av.id}
                  type="button"
                  onClick={() => updateForm({ ...form, avatar_id: av.id })}
                  title={av.description}
                  className="flex items-center gap-3 rounded-xl px-3 py-2 text-left transition-all"
                  style={{
                    border: selected
                      ? `2px solid ${form.primary_color}`
                      : "2px solid #E5E7EB",
                    backgroundColor: selected
                      ? `${form.primary_color}12`
                      : "#FFFFFF",
                  }}
                >
                  <img
                    src={av.thumb}
                    alt=""
                    className="w-11 h-11 rounded-full object-cover bg-gray-100 shrink-0"
                  />
                  <span className="min-w-0">
                    <span
                      className="block text-sm font-semibold leading-tight"
                      style={{
                        color: selected ? form.primary_color : "#374151",
                      }}
                    >
                      {av.label}
                    </span>
                    <span className="block text-xs text-gray-400 leading-tight mt-0.5">
                      {av.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            決定 widget 中顯示的形象，以及朗讀回覆時使用的語音。
          </p>
        </div>

        {/* Welcome Message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Welcome Message
          </label>
          <textarea
            value={form.welcome_message}
            onChange={(e) =>
              updateForm({
                ...form,
                welcome_message: e.target.value,
              })
            }
            rows={2}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E78A24] bg-white text-gray-900 resize-none"
            placeholder="Hi! How can I help you today?"
          />
        </div>

        {/* Style Preset */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Widget Style
          </label>
          <div className="flex gap-3">
            {[
              {
                value: "rounded",
                label: "Rounded",
                desc: "現代圓角",
              },
              {
                value: "sharp",
                label: "Sharp",
                desc: "商務方正",
              },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() =>
                  updateForm({
                    ...form,
                    style_preset: opt.value,
                  })
                }
                className="flex-1 py-2 px-3 rounded-lg border text-sm transition-all"
                style={{
                  borderColor:
                    form.style_preset === opt.value
                      ? form.primary_color
                      : "#D1D5DB",
                  backgroundColor:
                    form.style_preset === opt.value
                      ? `${form.primary_color}12`
                      : "white",
                  color:
                    form.style_preset === opt.value
                      ? form.primary_color
                      : "#6B7280",
                  fontWeight: form.style_preset === opt.value ? 600 : 400,
                }}
              >
                <div>{opt.label}</div>
                <div className="text-xs opacity-70">{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Starter Prompts */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Starter Prompts{" "}
            <span className="font-normal text-gray-400">
              （選填，最多 3 個）
            </span>
          </label>
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <input
                key={i}
                type="text"
                value={form.starter_prompts[i]}
                onChange={(e) => {
                  const updated = [...form.starter_prompts];
                  updated[i] = e.target.value;
                  updateForm({
                    ...form,
                    starter_prompts: updated,
                  });
                }}
                placeholder={`Prompt ${i + 1}，例如：How do I get started?`}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E78A24] bg-white text-gray-900"
              />
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Widget 開啟時顯示在對話框底部，引導用戶第一步。
          </p>
        </div>

        {/* Live preview */}
        {(() => {
          const previewRadius = form.style_preset === "sharp" ? 0 : 16;
          const previewColor = form.primary_color;
          const previewAvatar = getAvatar(form.avatar_id);
          const previewPrompts = form.starter_prompts.filter(Boolean);
          return (
            <div className="mt-6">
              <p className="text-xs text-gray-400 mb-3 uppercase tracking-wide font-medium">
                Live Preview
              </p>
              <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200">
                <div className="bg-gray-100 px-3 py-2 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 bg-white rounded px-2 py-0.5 text-xs text-gray-400 font-mono truncate">
                    your-website.com
                  </div>
                </div>
                <div className="relative bg-gray-50 h-72 p-4 overflow-hidden">
                  <div className="space-y-2 opacity-30">
                    {[80, 60, 70, 40, 65].map((w, i) => (
                      <div
                        key={i}
                        className="h-2 bg-gray-300 rounded"
                        style={{ width: `${w}%` }}
                      />
                    ))}
                  </div>
                  <div
                    className="absolute bottom-12 right-3 w-52 shadow-2xl overflow-hidden"
                    style={{
                      borderRadius: previewRadius,
                      border: `1.5px solid ${previewColor}40`,
                    }}
                  >
                    <div
                      className="flex items-center gap-2 px-3 py-2"
                      style={{
                        backgroundColor: previewColor,
                      }}
                    >
                      <div
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          backgroundColor: "rgba(255,255,255,0.25)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 14,
                          overflow: "hidden",
                        }}
                      >
                        <img
                          src={previewAvatar.thumb}
                          alt=""
                          className="w-full h-full rounded-full object-cover"
                        />
                      </div>
                      <span className="text-white text-xs font-semibold truncate flex-1">
                        {form.bot_name || "My Bot"}
                      </span>
                      <span className="text-white/70 text-xs">✕</span>
                    </div>
                    <div className="bg-white px-3 py-2 space-y-2">
                      <div className="flex justify-start">
                        <div
                          className="text-xs text-gray-700 bg-gray-100 px-2 py-1 max-w-[80%]"
                          style={{
                            borderRadius: previewRadius || 6,
                          }}
                        >
                          {form.welcome_message ||
                            "Hi! How can I help you today?"}
                        </div>
                      </div>
                      {previewPrompts.length > 0 && (
                        <div className="flex flex-wrap gap-1 pt-0.5">
                          {previewPrompts.slice(0, 2).map((p, i) => (
                            <span
                              key={i}
                              className="text-xs px-2 py-0.5"
                              style={{
                                borderRadius:
                                  form.style_preset === "sharp" ? 0 : 12,
                                border: `1px solid ${previewColor}`,
                                color: previewColor,
                              }}
                            >
                              {p.length > 14 ? p.slice(0, 14) + "…" : p}
                            </span>
                          ))}
                        </div>
                      )}
                      <div
                        className="flex items-center gap-1.5 border px-2 py-1 mt-1"
                        style={{
                          borderColor: previewColor,
                          borderRadius: previewRadius || 4,
                        }}
                      >
                        <span className="text-xs text-gray-400 flex-1">
                          Type a message…
                        </span>
                        <div
                          className="w-5 h-5 flex items-center justify-center"
                          style={{
                            backgroundColor: previewColor,
                            borderRadius: previewRadius || 4,
                          }}
                        >
                          <svg
                            className="w-3 h-3 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Launcher bubble — the real widget shows a chat glyph
                      here, not the avatar */}
                  <div
                    className="absolute bottom-3 right-3 w-9 h-9 rounded-full shadow-lg flex items-center justify-center text-white"
                    style={{
                      backgroundColor: previewColor,
                    }}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default SettingsTab;
